"use client";

import ListingFilterSelect from "@/app/components/filters/listing-filter-select";
import ListingSearchInput from "@/app/components/filters/listing-search-input";
import { useServicesFilterStore } from "@/app/stores/services-filter-store";
import { useState, useEffect, useCallback, useTransition } from "react";
import { getAllServiceCategories } from "@/app/services/client/get-all-service-categories";
import type { ServiceCategoryT } from "@/app/types/services";
import type { ContactFormSelectOptionT } from "@/app/components/forms/contact/contact-form-types";
import { useQueryState } from "nuqs";
import { searchParams } from "@/app/types/search-params";

interface ServicesFilterProps {
  locationOptions: ContactFormSelectOptionT[];
  allowAllLocations?: boolean;
  showLocationSelect?: boolean;
}

export default function ServicesFilter({
  locationOptions,
  allowAllLocations = true,
  showLocationSelect = true,
}: ServicesFilterProps) {
  const { selectedCategory, searchQuery, setSelectedCategory, setSearchQuery } =
    useServicesFilterStore();

  const [categories, setCategories] = useState<ServiceCategoryT[]>([]);
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);
  const [isSelectOpen, setIsSelectOpen] = useState(false);
  const [isStandortPending, startStandortTransition] = useTransition();
  const [selectedStandorte, setSelectedStandorte] = useQueryState<string[]>(
    "standort",
    searchParams.standort.withOptions({
      shallow: false,
      startTransition: startStandortTransition,
    })
  );

  const selectedStandortValue = selectedStandorte[0] || "all";
  const locationSelectOptions = [
    ...(allowAllLocations ? [{ value: "all", label: "Alle Standorte" }] : []),
    ...locationOptions,
  ];
  const categoryOptions = [
    { value: "all", label: "Alle Kategorien" },
    ...categories.map((category) => ({
      value: category.key,
      label: category.label,
    })),
  ];

  // Load categories function
  const loadCategories = useCallback(async () => {
    if (categories.length > 0 || isLoadingCategories) return;

    setIsLoadingCategories(true);
    try {
      const fetchedCategories = await getAllServiceCategories();
      setCategories(fetchedCategories || []);
    } catch (error) {
      console.error("Fehler beim Laden der Kategorien:", error);
    } finally {
      setIsLoadingCategories(false);
    }
  }, [categories.length, isLoadingCategories]);

  // Lazy load categories when select is opened
  useEffect(() => {
    if (isSelectOpen) {
      loadCategories();
    }
  }, [isSelectOpen, loadCategories]);

  const handleStandortChange = (value: string) => {
    if (value === "all") {
      setSelectedStandorte([]);
      return;
    }
    setSelectedStandorte([value]);
  };

  return (
    <div
      className={`mt-6 grid grid-cols-1 gap-4 ${
        showLocationSelect ? "md:grid-cols-3" : "md:grid-cols-2"
      }`}
    >
      {showLocationSelect && (
        <div data-pending={isStandortPending ? "" : undefined}>
          <ListingFilterSelect
            value={selectedStandortValue}
            onValueChange={handleStandortChange}
            placeholder="Standort"
            label="Standort"
            options={locationSelectOptions}
          />
        </div>
      )}

      <ListingFilterSelect
        value={selectedCategory}
        onValueChange={setSelectedCategory}
        onOpenChange={setIsSelectOpen}
        onMouseEnter={loadCategories}
        placeholder="Kategorie"
        label="Kategorie"
        options={categoryOptions}
        isLoading={isLoadingCategories}
      />

      <ListingSearchInput value={searchQuery} onChange={setSearchQuery} />
    </div>
  );
}
